import axios from 'axios';

const API_BASE_URL = '/api';
const TOKEN_KEY = 'admin_token';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (username: string, password: string) =>
    axios.post(`${API_BASE_URL}/auth/login`, { username, password }),
  me: (token: string) =>
    axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Cars API
export const carsApi = {
  getPending: () => apiClient.get('/cars/pending'),
  getById: (id: string) => apiClient.get(`/cars/${id}`),
  update: (id: string, data: any) => apiClient.put(`/cars/${id}`, data),
  updateStatus: (id: string, status: string, reason?: string) => 
    apiClient.put(`/cars/${id}/status`, { status, reason }),
  getStatistics: () => apiClient.get('/cars/statistics'),
};

// Payments API
export const paymentsApi = {
  getPending: () => apiClient.get('/payments/pending'),
  confirm: (id: string, adminId: string, note?: string) => 
    apiClient.put(`/payments/${id}/confirm`, { adminId, note }),
  reject: (id: string, adminId: string, note?: string) => 
    apiClient.put(`/payments/${id}/reject`, { adminId, note }),
  getStatistics: () => apiClient.get('/payments/statistics'),
  grantFreePosts: (userId: string, count: number) =>
    apiClient.post('/payments/free-posts', { userId, count }),
};

// Admins API
export const adminsApi = {
  getAll: () => apiClient.get('/admins'),
  create: (telegramId: string, username?: string, role?: string) => 
    apiClient.post('/admins', { telegramId, username, role }),
  update: (id: string, data: any) => apiClient.put(`/admins/${id}`, data),
  delete: (id: string) => apiClient.delete(`/admins/${id}`),
  getSettings: () => apiClient.get('/admins/settings'),
  updateSetting: (key: string, value: string) => 
    apiClient.put(`/admins/settings/${key}`, { value }),
};

// Users API
export const usersApi = {
  getAll: () => apiClient.get('/users'),
  getById: (id: string) => apiClient.get(`/users/${id}`),
  resetAudioUsage: (id: string) => apiClient.post(`/users/${id}/reset-audio`),
  toggleBlock: (id: string) => apiClient.post(`/users/${id}/toggle-block`),
  grantFreePosts: (id: string, count: number) => 
    apiClient.post(`/users/${id}/free-posts`, { count }),
};
